import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { FileDetails } from './FileDetails';
import type { EntityState } from './../enums/EntityState';

export class EventLiveLogos extends BaseObj {
    public readonly ChipControlAndResultLogo: FileDetails | null;

    public readonly DashboardBackground: FileDetails | null;

    public readonly DashboardBackgroundMobile: FileDetails | null;

    public readonly EventLogo: FileDetails | null;

    public PartnerLogos: FileDetails[];
    public __partnerlogos: FileDetails[];

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ChipControlAndResultLogo?: FileDetails | null;
        DashboardBackground?: FileDetails | null;
        DashboardBackgroundMobile?: FileDetails | null;
        EntityState?: EntityState;
        EventLogo?: FileDetails | null;
        PartnerLogos?: FileDetails[];
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<EventLiveLogos>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('EventLiveLogos tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ChipControlAndResultLogo = values.ChipControlAndResultLogo ?? null;
        this.ChipControlAndResultLogo = values.ChipControlAndResultLogo ?? null;
        this.ChipControlAndResultLogo?._setNotifyParentOfModificationCallback(() =>
            this.__entityStateHandler.childBaseObjChanged('ChipControlAndResultLogo', this.ChipControlAndResultLogo),
        );

        this.DashboardBackground = values.DashboardBackground ?? null;
        this.DashboardBackground = values.DashboardBackground ?? null;
        this.DashboardBackground?._setNotifyParentOfModificationCallback(() =>
            this.__entityStateHandler.childBaseObjChanged('DashboardBackground', this.DashboardBackground),
        );

        this.DashboardBackgroundMobile = values.DashboardBackgroundMobile ?? null;
        this.DashboardBackgroundMobile = values.DashboardBackgroundMobile ?? null;
        this.DashboardBackgroundMobile?._setNotifyParentOfModificationCallback(() =>
            this.__entityStateHandler.childBaseObjChanged('DashboardBackgroundMobile', this.DashboardBackgroundMobile),
        );

        this.EventLogo = values.EventLogo ?? null;
        this.EventLogo = values.EventLogo ?? null;
        this.EventLogo?._setNotifyParentOfModificationCallback(() =>
            this.__entityStateHandler.childBaseObjChanged('EventLogo', this.EventLogo),
        );

        this.PartnerLogos = values.PartnerLogos ?? [];
        this.__partnerlogos = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.PartnerLogos ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('PartnerLogos', newVal),
        });
        this.__partnerlogos.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('PartnerLogos', this.PartnerLogos),
            );
        });
        Object.defineProperty(this, 'PartnerLogos', {
            get(): FileDetails[] {
                return this.__partnerlogos;
            },
            set(value: FileDetails[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__partnerlogos = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: FileDetails[], newVal: FileDetails[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('PartnerLogos', newVal),
                });
                this.__partnerlogos.forEach((child: FileDetails) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('PartnerLogos', this.PartnerLogos),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('PartnerLogos', this.PartnerLogos);
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ChipControlAndResultLogo": ${!this.ChipControlAndResultLogo ? null : this.ChipControlAndResultLogo?.serialize()}` +
            ',' +
            `"DashboardBackground": ${!this.DashboardBackground ? null : this.DashboardBackground?.serialize()}` +
            ',' +
            `"DashboardBackgroundMobile": ${!this.DashboardBackgroundMobile ? null : this.DashboardBackgroundMobile?.serialize()}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventLogo": ${!this.EventLogo ? null : this.EventLogo?.serialize()}` +
            ',' +
            `"PartnerLogos": [ ${this.PartnerLogos.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
                     * Sets the entity state to Added
                     *
                     
                     * 
                     * Note: This does not do anything with the children, so use with caution
                     * 
                     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
                     */
    public SetEntityStateAsAddedAndResetId() {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';

        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of EventLiveLogos from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ChipControlAndResultLogo?: FileDetails | null;
        DashboardBackground?: FileDetails | null;
        DashboardBackgroundMobile?: FileDetails | null;
        EventLogo?: FileDetails | null;
        PartnerLogos?: FileDetails[];
        EntityState?: 'Added' | 'Unchanged';
    }): EventLiveLogos {
        return new EventLiveLogos(values);
    }

    public static _FromServerJson(json: any): EventLiveLogos {
        return new EventLiveLogos({
            ChipControlAndResultLogo: json.ChipControlAndResultLogo ? FileDetails._FromServerJson(json.ChipControlAndResultLogo) : null,
            DashboardBackground: json.DashboardBackground ? FileDetails._FromServerJson(json.DashboardBackground) : null,
            DashboardBackgroundMobile: json.DashboardBackgroundMobile ? FileDetails._FromServerJson(json.DashboardBackgroundMobile) : null,
            EventLogo: json.EventLogo ? FileDetails._FromServerJson(json.EventLogo) : null,
            PartnerLogos: json.PartnerLogos?.map((x: any) => FileDetails._FromServerJson(x)),
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of EventLiveLogos from another instance of EventLiveLogos.
     * @param other The EventLiveLogos you want to clone
     * @returns A cloned EventLiveLogos
     */
    public static FromOther(other: EventLiveLogos): EventLiveLogos {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return EventLiveLogos._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return EventLiveLogos.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: ('ChipControlAndResultLogo' | 'DashboardBackground' | 'DashboardBackgroundMobile' | 'EventLogo' | 'PartnerLogos')[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'EventLiveLogos';
}
